import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import { Banner, Button, Text } from '@hh.ru/magritte-ui';
import { makeSetStoreField } from '@hh.ru/redux-create-reducer/lib/createReducer';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { useBannerClose } from 'src/components/Search/Resume/ContextVacancy/hooks/useBannerClose';
import {
    AnalyticsParams,
    ResumeSearchContextAction,
    ResumeSearchContextSource,
    sendEventAction,
    sendEventElementShown,
} from 'src/components/Search/Resume/ContextVacancy/utils/analytics';
import translation from 'src/components/translation';

const setContextVacancySelectorVisible = makeSetStoreField('resumeSearchContextVacancySelectorVisible');

const TrlKeys = {
    title: 'resume.search.context.banner.withoutVacancy.title',
    description: 'resume.search.context.banner.withoutVacancy.description',
    actionSelect: 'resume.search.context.banner.withoutVacancy.action.select',
};

interface Props {
    employerId: string;
    employerManagerId: string;
    isMobile: boolean;
}

const ContextBannerWithoutVacancy: TranslatedComponent<Props> = ({ employerId, employerManagerId, isMobile, trls }) => {
    const dispatch = useDispatch();

    const handleClose = useBannerClose();

    const analyticsParams: AnalyticsParams = useMemo(
        () => ({
            employerId,
            employerManagerId,
            hhtmSourceLabel: ResumeSearchContextSource.Banner,
        }),
        [employerId, employerManagerId]
    );

    return (
        <ElementShownAnchor fn={sendEventElementShown} {...analyticsParams}>
            <Banner
                stretched
                showBorder
                title={<Text typography="title-5-semibold">{trls[TrlKeys.title]}</Text>}
                content={<Text typography="paragraph-2-regular">{trls[TrlKeys.description]}</Text>}
                buttonBase={
                    <Button
                        style="accent"
                        mode="primary"
                        size="small"
                        stretched={isMobile}
                        onClick={() => {
                            dispatch(setContextVacancySelectorVisible(true));
                            sendEventAction(ResumeSearchContextAction.Select, analyticsParams);
                        }}
                        data-qa="resume-search-context-action-select"
                    >
                        {trls[TrlKeys.actionSelect]}
                    </Button>
                }
                showClose
                onClose={handleClose}
                data-qa="resume-search-context-banner-winthout-vacancy"
            />
        </ElementShownAnchor>
    );
};

export default translation(ContextBannerWithoutVacancy);
